import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { useVehicles } from '../../../hooks/useVehicles';
import { useMemo } from 'react';

export default function ImpactTimeline() {
  const { ref, isVisible } = useScrollAnimation();
  const { vehicles, loading } = useVehicles();

  const { soldCount, totalDonated } = useMemo(() => {
    const sold = vehicles.filter(v => v.status === 'sold').length;
    return {
      soldCount: sold,
      totalDonated: sold * 100,
    };
  }, [vehicles]);

  const milestones = [
    {
      year: 'Today',
      title: 'Partnership Launched',
      description: loading
        ? 'Loading current impact...'
        : `${soldCount === 0
            ? '$0 donated — growing with every sale. Each vehicle sold contributes $100 to Oregon Dog Rescue.'
            : `$${totalDonated.toLocaleString()} donated from ${soldCount} ${soldCount === 1 ? 'vehicle' : 'vehicles'} sold. Every sale contributes $100 to help rescue dogs find forever homes.`}`,
      icon: 'ri-handshake-line',
    },
  ];

  return (
    <section className="py-20 bg-[#F7F5F0]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          ref={ref}
          className={`text-center mb-12 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="font-['Oswald'] text-3xl sm:text-4xl md:text-5xl font-bold text-[#1C2B1E] mb-4">
            Our Commitment
          </h2>
          <div className="w-24 h-1 bg-[#E8B84B] mx-auto mb-6"></div>
          <p className="text-base sm:text-lg max-w-2xl mx-auto" style={{ color: '#374151' }}>
            Starting fresh with transparency and honesty. Every milestone will be earned through real sales and genuine impact.
          </p>
        </div>

        <div className="relative">
          <div aria-hidden="true" className="absolute left-8 top-0 bottom-0 w-[0.5px] bg-[#E8B84B] hidden md:block"></div>

          <div className="space-y-10">
            {milestones.map((milestone, index) => (
              <div
                key={index}
                className={`relative transition-all duration-1000 ${
                  isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
                }`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <div className="flex items-start gap-6">
                  <div aria-hidden="true" className="hidden md:flex w-16 h-16 bg-[#E8B84B] rounded-full items-center justify-center flex-shrink-0 relative z-10">
                    <i className={`${milestone.icon} text-2xl text-white`}></i>
                  </div>

                  <div className="flex-1 bg-white rounded-xl p-6 sm:p-8 shadow-md border border-[#E8E2D6] hover:shadow-lg transition-shadow duration-300">
                    <div className="flex items-center gap-4 mb-3">
                      <div aria-hidden="true" className="md:hidden w-10 h-10 bg-[#E8B84B] rounded-full flex items-center justify-center">
                        <i className={`${milestone.icon} text-lg text-white`}></i>
                      </div>
                      <span className="font-['Oswald'] text-2xl sm:text-3xl font-bold text-[#E8B84B]">
                        {milestone.year}
                      </span>
                    </div>
                    <h3 className="font-['Oswald'] text-xl sm:text-2xl font-bold text-[#1C2B1E] mb-2">
                      {milestone.title}
                    </h3>
                    <p style={{ color: '#374151' }} className="leading-relaxed text-sm sm:text-base">{milestone.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
